import { clearReferral, referralSignupFields } from '@/lib/referral'
import { SdkContext, type Spec } from '../internal'
import { solveAltcha } from '@/lib/altcha'
import type { Challenge } from 'altcha-lib/types'
import type { SmsConsentChoice } from './sms'
import type { TwoFactorChallenge, TwoFactorChannel, TwoFactorSummary } from './two-factor'

/** SEC-173: the API refused the ALTCHA (422) or cannot check it now (503). */
export const isHumanCheckError = (e: unknown, status?: number): boolean => {
  const err = e as { response?: { status?: number; data?: { errors?: Array<{ param?: string }> } } } | null
  const errors = err?.response?.data?.errors
  return (status === undefined || err?.response?.status === status) &&
    Array.isArray(errors) && errors[0]?.param === 'altcha'
}

export interface SignInData {
  email: string
  password?: string
  /** F-142: the consent text shown next to "Entrar" (once per community, never blocking). */
  smsConsent?: SmsConsentChoice
  /** F-233: the token of a browser remembered after the code step (90 days). */
  deviceToken?: string | null
  /** F-233: the channel of the first code (WhatsApp by default). */
  twoFactorChannel?: TwoFactorChannel
}

/** SEC-173: called once the challenge is in hand, with its work level (1 normal, up to 10 after failed logins). */
export type HumanCheckProgress = (level: number) => void

export interface SignUpData {
  email: string
  name: string
  password: string
  /** F-85: the community's signup fields, `{ key: value }`. */
  fields?: Record<string, string | string[]>
  /** F-142: optional phone, unconfirmed until the code. */
  phone?: string
  phoneCountry?: string
  /** F-142: the service text next to the button, and the separate unchecked marketing box. */
  smsConsent?: SmsConsentChoice
  /** F-204: the person confirmed the community's minimum age (16, or its higher `minimumAge`). */
  ageConfirmed?: boolean
}

/** The session's member: one community, where the token holds (F-31). */
export interface SessionMember {
  /** The member id. */
  id: string
  communityId?: string
  role?: string
  displayName?: string | null
  fullName: string
  email: string
  avatarUrl: string | null
  isSuperAdmin?: boolean
  joinedAt?: string
}

export interface AuthResponse {
  member: SessionMember
  token: string
  mustChangePassword?: boolean
  /** F-233: false here (the code step is `TwoFactorChallenge`); `twoFactor` says whether the account must turn it on. */
  twoFactorRequired?: false
  twoFactor?: TwoFactorSummary
}

/** F-233: what the password step answers: a session, or the code step first. */
export type SignInResult = AuthResponse | TwoFactorChallenge

export const isTwoFactorChallenge = (answer: SignInResult): answer is TwoFactorChallenge =>
  (answer as TwoFactorChallenge).twoFactorRequired === true

/** `GET /auth/me`: the session's member, flat, in the `X-CommunityId`'s community. */
export interface MeResponse {
  /** The member id. */
  id: string
  fullName: string
  email: string
  avatarUrl: string | null
  role?: string
  displayName?: string | null
  tagline?: string | null
  jobTitle?: string | null
  phone?: string | null
  phoneCountry?: string | null
  language?: string | null
  joinedAt?: string
  /**
   * SuperAdmin in the X-CommunityId's community: true only in the platform
   * ("admin") community (F-31 F2). Optional: older API builds did not send it (APP-39).
   */
  isSuperAdmin?: boolean
  communityId?: string
  /** CPF or CNPJ on the account, digits only; null when none. Only the member reads it. */
  document?: string | null
  documentType?: 'CPF' | 'CNPJ' | null
  /** F-93: the company that gives the person access (active plans or products), or `null`. */
  viaCompany?: { companyId: string; name: string; plans: string[] } | null
  /** F-233: the account's two-step verification here (on, must have it, the team's deadline). */
  twoFactor?: TwoFactorSummary
  [key: string]: unknown
}

export class AuthResource {
  constructor(private ctx: SdkContext) {}

  /**
   * SEC-173: login, sign-up and recovery each carry a fresh ALTCHA solution,
   * solved invisibly here (about a second). A 422 on `altcha` (expired, used)
   * gets one new challenge and one resend; a 429 keeps `retryAfter` (seconds).
   * Login asks for its challenge by `POST /auth/challenge { email }` (never
   * in the URL): after failed logins the API makes that e-mail's work heavier
   * (`parameters.data.level`), and a 422 `auth.humanCheckHarder` asks for a
   * new, heavier one, which the single resend covers too.
   */
  private async withHumanCheck<T>(
    spec: Spec,
    body: Record<string, unknown>,
    challengeBody?: Record<string, string>,
    onProgress?: HumanCheckProgress
  ): Promise<T> {
    const send = async () => {
      const challenge = challengeBody
        ? await this.ctx.call<Challenge>({ httpMethod: 'POST', path: '/auth/challenge', hasBody: true }, { body: challengeBody })
        : await this.ctx.call<Challenge>({ httpMethod: 'GET', path: '/auth/challenge' })
      const level = Number((challenge.parameters?.data as { level?: unknown } | undefined)?.level) || 1
      onProgress?.(level)
      const altcha = await solveAltcha(challenge)
      return this.ctx.callKeepingRetryAfter<T>(spec, { body: { ...body, altcha } })
    }
    try {
      return await send()
    } catch (e) {
      if (!isHumanCheckError(e, 422)) throw e
      return send()
    }
  }

  async signIn({ email, password, smsConsent, deviceToken, twoFactorChannel }: SignInData, onProgress?: HumanCheckProgress): Promise<SignInResult> {
    return this.withHumanCheck<SignInResult>(
      { httpMethod: 'POST', path: '/auth/login', hasBody: true },
      {
        email, password,
        ...(smsConsent ? { smsConsent } : {}),
        ...(deviceToken ? { deviceToken } : {}),
        ...(twoFactorChannel ? { twoFactorChannel } : {})
      },
      { email },
      onProgress
    )
  }

  async signUp({ email, name, password, fields, phone, phoneCountry, smsConsent, ageConfirmed }: SignUpData): Promise<AuthResponse> {
    return this.withHumanCheck<AuthResponse>(
      { httpMethod: 'POST', path: '/auth/register', hasBody: true },
      {
        fullName: name, email, password, acceptedTerms: true,
        ...(ageConfirmed !== undefined ? { ageConfirmed } : {}),
        ...(fields && Object.keys(fields).length ? { fields } : {}),
        ...(phone ? { phone, ...(phoneCountry ? { phoneCountry } : {}) } : {}),
        ...(smsConsent ? { smsConsent } : {}),
        ...referralSignupFields()
      }
    ).then((auth) => { clearReferral(); return auth })
  }

  /**
   * F-150: asks to join a community that approves signups (no password). The
   * answer is the same 202 whether a request was created or not.
   */
  async requestToJoin({ email, name, language, fields, phone, phoneCountry, ageConfirmed }: Omit<SignUpData, 'password' | 'smsConsent'> & { language?: string }): Promise<{ status: 'pending' }> {
    return this.withHumanCheck<{ status: 'pending' }>(
      { httpMethod: 'POST', path: '/auth/signup-request', hasBody: true },
      {
        fullName: name, email,
        ...(ageConfirmed !== undefined ? { ageConfirmed } : {}),
        ...(language ? { language } : {}),
        ...(fields && Object.keys(fields).length ? { fields } : {}),
        ...(phone ? { phone, ...(phoneCountry ? { phoneCountry } : {}) } : {}),
        ...referralSignupFields()
      }
    ).then((answer) => { clearReferral(); return answer })
  }

  async me(): Promise<MeResponse> {
    return this.ctx.call<MeResponse>({ httpMethod: 'GET', path: '/auth/me' })
  }

  /**
   * PROF-3: the invite's single-use link sets the password; answers `{ email }`, opens no session.
   * F-204: `ageConfirmed` is the box "I am N or older" on that screen.
   */
  async setPassword(id: string, token: string, password: string, ageConfirmed?: boolean): Promise<{ email: string }> {
    return this.ctx.call<{ email: string }>({
      httpMethod: 'POST',
      path: '/auth/set-password',
      hasBody: true
    }, { body: { id, token, password, acceptedTerms: true, ...(ageConfirmed !== undefined ? { ageConfirmed } : {}) } })
  }

  /**
   * F-31 F2: trades the one-time "Entrar" link (`/support-entry#token=…`) for
   * the community owner's session, in the login's shape. The community is the
   * `X-CommunityId` of this domain; a used, expired or other-community link is 400.
   */
  async supportEntry(token: string): Promise<AuthResponse> {
    return this.ctx.call<AuthResponse>({
      httpMethod: 'POST',
      path: '/auth/support-entry',
      hasBody: true
    }, { body: { token } })
  }

  /**
   * F-199: gives up an account erasure with the login's e-mail and password, no session
   * (the login answered `ERASURE_SCHEDULED`). Same ALTCHA as the login, on a challenge for
   * that e-mail (SEC-326); a wrong password is 404 and counts as a failed login.
   */
  async cancelErasureWithLogin(email: string, password: string, onProgress?: HumanCheckProgress): Promise<unknown> {
    return this.withHumanCheck<unknown>(
      { httpMethod: 'POST', path: '/privacy/erasure/cancel', hasBody: true },
      { email, password },
      { email },
      onProgress
    )
  }

  async recoveryPassword(email: string): Promise<{ message: string }> {
    return this.withHumanCheck<{ message: string }>({ httpMethod: 'POST', path: '/auth/recovery-password', hasBody: true }, { email })
  }

  /**
   * F-31 F3: answers `{ token }`, the session's new token: the change ends every
   * other session of the account, the one that asked included, so the caller must
   * store it (AuthContext.changePassword does).
   */
  async changePassword({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }): Promise<{ token?: string }> {
    return this.ctx.call<{ token?: string }>({
      httpMethod: 'PUT',
      path: '/auth/change-password',
      hasBody: true
    }, { body: { currentPassword, newPassword } })
  }
}
